import { Fragment } from "react";
import { Badge, Button, CardBody, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  IoAddOutline, IoCalendarOutline, IoCallOutline, IoCashOutline, IoDocumentTextOutline,
  IoLocationOutline, IoPersonOutline, IoReceiptOutline
} from "react-icons/io5";
import { useSettings } from "../../../../../../context/settings";
import { formatMoney, useCurrency } from "../../../../../../utils/helpers/currency";
import { printInvoiceStatus, printQuoteStatus } from "../../../../../Sales/utils/helpers";
import NotesSection from "../../../../components/Sections/Notes";
import AttachmentsSection from "../../../../components/Sections/Attachments";
import { EmptyState, Field, SectionCard } from "../../../../components/Record";

function TableFooterNote({ shown, total }) {
  const { t } = useTranslation();

  if (!total || total <= shown) return null;

  return (
    <div className="text-muted text-center py-2" style={{ fontSize: '0.75rem' }}>
      {t('crm.showingLatest', { shown, total })}
    </div>
  );
}

function OverviewTab({ customerData, financials = {} }) {
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useSettings();
  const currency = useCurrency();
  const navigate = useNavigate();

  const money = (value) => `${formatMoney(value)} ${currency}`;

  const contacts = customerData?.contacts ?? [];
  const addresses = customerData?.addresses ?? [];
  const invoices = customerData?.invoices ?? [];
  const quotes = customerData?.quotes ?? [];
  const payments = customerData?.payments ?? [];
  const meetings = customerData?.meetings ?? [];

  const fullName = [customerData?.firstname, customerData?.middlename, customerData?.lastname]
    .filter(Boolean).join(' ');

  const addNewButton = (label, to, state) => (
    <Button
      size="sm"
      outline
      color="secondary"
      className="d-flex align-items-center gap-1 flex-shrink-0"
      onClick={() => navigate(to, state ? { state } : undefined)}
    >
      <IoAddOutline size={14} />
      {label}
    </Button>
  );

  return (
    <Fragment>
      {/* General information */}
      <SectionCard
        id="general-information-card"
        icon={<IoPersonOutline size={15} />}
        title={t('crm.generalInformation')}
      >
        <CardBody>
          <Row className="g-3">
            <Field label={t('crm.contactOwner')} value={fullName} />
            <Field label={t('common.type')} value={customerData?.type ? t('crm.' + customerData.type) : null} />
            <Field label={t('common.companyName')} value={customerData?.company_name} />
            <Field
              label={t('common.email')}
              value={customerData?.primary_email}
              href={`mailto:${customerData?.primary_email}`}
            />
            <Field
              label={t('common.phone')}
              value={[customerData?.country_code, customerData?.primary_phone].filter(Boolean).join(' ')}
              href={`tel:${customerData?.primary_phone}`}
            />
            <Field label={t('common.country')} value={customerData?.country} />
            <Field label={t('legal.rc')} value={customerData?.registration_number} />
            <Field label={t('legal.nif')} value={customerData?.tax_id} />
            <Field label={t('legal.nis')} value={customerData?.nis} />
            <Field label={t('legal.taxArticle')} value={customerData?.tax_article} />
            <Field label={t('crm.customerSince')} value={formatDate(customerData?.created_at)} />
          </Row>
        </CardBody>
      </SectionCard>

      {/* Contacts */}
      <SectionCard
        id="contact-information-card"
        icon={<IoCallOutline size={15} />}
        title={t('crm.contactInformation')}
        count={contacts.length}
      >
        {contacts.length === 0 ? (
          <EmptyState message={t('crm.noRecordsYet')} />
        ) : (
          <div className="table-responsive">
            <table className="table crm-record-table">
              <thead>
                <tr>
                  <th>{t('common.type')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.email')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('timeline.createdBy')}</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.id}>
                    <td>{contact.label ? <Badge color="light">{contact.label}</Badge> : '-'}</td>
                    <td className="fw-semibold">{contact.responsible_name || '-'}</td>
                    <td>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-decoration-none">{contact.email}</a>
                      ) : '-'}
                    </td>
                    <td>{[contact.country_code, contact.phone_number].filter(Boolean).join(' ') || '-'}</td>
                    <td className="text-muted">{contact.created_by?.firstname || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Addresses */}
      <SectionCard
        id="address-card"
        icon={<IoLocationOutline size={15} />}
        title={t('common.address')}
        count={addresses.length}
      >
        {addresses.length === 0 ? (
          <EmptyState message={t('crm.noRecordsYet')} />
        ) : (
          <CardBody>
            <Row className="g-3">
              {addresses.map(address => (
                <Col md="6" key={address.id}>
                  <div className="border rounded-3 p-3 h-100">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <Badge color="light">{address.label || t('common.address')}</Badge>
                      <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {address.created_by?.firstname || ''}
                      </span>
                    </div>
                    <div className="crm-record-value">{address.street || '-'}</div>
                    <div className="crm-record-value text-muted">
                      {[address.postal_code, address.city, address.state].filter(Boolean).join(' ') || '-'}
                    </div>
                    <div className="crm-record-value text-muted">{address.country || '-'}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </CardBody>
        )}
      </SectionCard>

      <NotesSection
        objectData={customerData}
        objectType="customer"
      />

      <AttachmentsSection
        objectData={customerData}
        objectType="customer"
      />

      {/* Invoices */}
      <SectionCard
        id="invoices-card"
        icon={<IoReceiptOutline size={15} />}
        title={t('sales.invoices')}
        count={financials.invoices_count ?? invoices.length}
        action={addNewButton(t('sales.newInvoice'), '/sales/invoices/create?customer_id=' + customerData?.id)}
      >
        {invoices.length === 0 ? (
          <EmptyState message={t('crm.noRecordsYet')} />
        ) : (
          <Fragment>
            <div className="table-responsive">
              <table className="table crm-record-table">
                <thead>
                  <tr>
                    <th>{t('sales.invoiceNumber')}</th>
                    <th>{t('sales.invoiceDate')}</th>
                    <th>{t('sales.dueDate')}</th>
                    <th>{t('common.status')}</th>
                    <th className="text-end">{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr key={invoice.id} className="is-clickable" onClick={() => navigate('/sales/invoices/edit/' + invoice.id)}>
                      <td className="fw-semibold">{invoice.invoice_number}</td>
                      <td className="text-muted">{formatDate(invoice.invoice_date) || '-'}</td>
                      <td className="text-muted">{formatDate(invoice.due_date) || '-'}</td>
                      <td>{printInvoiceStatus(invoice.status)}</td>
                      <td className="text-end fw-bold">{money(invoice.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableFooterNote shown={invoices.length} total={financials.invoices_count} />
          </Fragment>
        )}
      </SectionCard>

      {/* Quotes */}
      <SectionCard
        id="quotes-card"
        icon={<IoDocumentTextOutline size={15} />}
        title={t('sales.quotes.title')}
        count={financials.quotes_count ?? quotes.length}
        action={addNewButton(t('sales.newQuote'), '/sales/quotes/create?customer_id=' + customerData?.id)}
      >
        {quotes.length === 0 ? (
          <EmptyState message={t('crm.noRecordsYet')} />
        ) : (
          <Fragment>
            <div className="table-responsive">
              <table className="table crm-record-table">
                <thead>
                  <tr>
                    <th>{t('sales.quoteNumber')}</th>
                    <th>{t('sales.quoteDate')}</th>
                    <th>{t('common.status')}</th>
                    <th className="text-end">{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map(quote => (
                    <tr key={quote.id} className="is-clickable" onClick={() => navigate('/sales/quotes/edit/' + quote.id)}>
                      <td className="fw-semibold">{quote.quote_number}</td>
                      <td className="text-muted">{formatDate(quote.quote_date) || '-'}</td>
                      <td>{printQuoteStatus(quote.status)}</td>
                      <td className="text-end fw-bold">{money(quote.total_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableFooterNote shown={quotes.length} total={financials.quotes_count} />
          </Fragment>
        )}
      </SectionCard>

      {/* Payments */}
      <SectionCard
        id="payments-card"
        icon={<IoCashOutline size={15} />}
        title={t('sales.payments')}
        count={financials.payments_count ?? payments.length}
        action={addNewButton(
          t('sales.newPayment'),
          '/sales/payments/create',
          { paymentData: { customer_id: customerData?.id } }
        )}
      >
        {payments.length === 0 ? (
          <EmptyState message={t('crm.noRecordsYet')} />
        ) : (
          <Fragment>
            <div className="table-responsive">
              <table className="table crm-record-table">
                <thead>
                  <tr>
                    <th>{t('common.reference')}</th>
                    <th>{t('sales.paymentMethod')}</th>
                    <th>{t('sales.paymentDate')}</th>
                    <th className="text-end">{t('common.amountReceived')}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(payment => (
                    <tr key={payment.id} className="is-clickable" onClick={() => navigate('/sales/payments/edit/' + payment.id)}>
                      <td className="fw-semibold">{payment.reference || '-'}</td>
                      <td className="text-muted">{payment.payment_method || '-'}</td>
                      <td className="text-muted">{formatDate(payment.payment_date) || '-'}</td>
                      <td className="text-end fw-bold text-success">{money(payment.amount_received)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableFooterNote shown={payments.length} total={financials.payments_count} />
          </Fragment>
        )}
      </SectionCard>

      {/* Meetings */}
      <SectionCard
        id="meetings-card"
        icon={<IoCalendarOutline size={15} />}
        title={t('crm.meetings')}
        count={financials.meetings_count ?? meetings.length}
        action={addNewButton(
          t('crm.newMeeting'),
          '/crm/meetings/create',
          { meetingData: { related_to: 'customer', customer_id: customerData?.id } }
        )}
      >
        {meetings.length === 0 ? (
          <EmptyState message={t('crm.noRecordsYet')} />
        ) : (
          <Fragment>
            <div className="table-responsive">
              <table className="table crm-record-table">
                <thead>
                  <tr>
                    <th>{t('common.title')}</th>
                    <th>{t('crm.meetingLocation')}</th>
                    <th>{t('crm.startTime')}</th>
                    <th>{t('crm.endTime')}</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map(meeting => (
                    <tr key={meeting.id} className="is-clickable" onClick={() => navigate('/crm/meetings/view/' + meeting.id)}>
                      <td className="fw-semibold">{meeting.title || '-'}</td>
                      <td className="text-muted">{meeting.location || '-'}</td>
                      <td className="text-muted">{formatDateTime(meeting.time_from) || '-'}</td>
                      <td className="text-muted">{formatDateTime(meeting.time_to) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableFooterNote shown={meetings.length} total={financials.meetings_count} />
          </Fragment>
        )}
      </SectionCard>
    </Fragment>
  );
}

export default OverviewTab;
